export interface InvoiceTimeParts {
    hour: number;
    minute: number;
}

export const INVOICE_MINUTE_OPTIONS = Array.from({ length: 12 }, (_, index) => index * 5);
export const INVOICE_UNPAID_MINUTES = Array.from({ length: 25 }, (_, index) => index * 5);

// MySQL DECIMAL(8,6) rounds thirds of an hour. Normalize persisted values to
// the matching five-minute picker option when an invoice is reopened.
export function invoiceDurationPickerValue(hours: number | string | null | undefined): string {
    if (hours === null || hours === undefined || hours === '') return '';
    const value = Number(hours);
    if (!Number.isFinite(value)) return '';
    const minutes = Math.round(value * 12) * 5;
    return String(minutes / 60);
}

export function parseInvoiceTime(value: string | null | undefined): InvoiceTimeParts | null {
    if (!value) return null;

    const match = value.trim().match(/^(\d{1,2}):(\d{1,2})(?::\d{2})?\s*(am|pm)?$/i);
    if (match) {
        let hour = Number(match[1]);
        const minute = Number(match[2]);
        const period = match[3]?.toLowerCase();
        if (minute > 59 || (period ? hour < 1 || hour > 12 : hour > 23)) return null;
        if (period) hour = hour % 12 + (period === 'pm' ? 12 : 0);
        return { hour, minute };
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : { hour: date.getHours(), minute: date.getMinutes() };
}

export function formatInvoiceTime({ hour, minute }: InvoiceTimeParts): string {
    return `${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'pm' : 'am'}`;
}

export function getInvoiceHourOptions(postedStart: string): number[] {
    const postedHour = parseInvoiceTime(postedStart)?.hour ?? 9;
    const earliestHour = (postedHour + 23) % 24;
    return Array.from({ length: 24 - earliestHour }, (_, index) => earliestHour + index);
}

export function formatInvoiceHour(hour: number): string {
    return `${hour % 12 || 12} ${hour >= 12 ? 'PM' : 'AM'}`;
}

export function formatUnpaidMinutes(minutes: number): string {
    if (minutes === 0) return '0 minutes';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const hourLabel = hours ? `${hours} ${hours === 1 ? 'hour' : 'hours'}` : '';
    const minuteLabel = remainingMinutes ? `${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}` : '';
    return [hourLabel, minuteLabel].filter(Boolean).join(' ');
}

export function calculateInvoiceBreakdown(
    totalHours: number,
    lunchHours: number,
    downtimeHours: number,
    gapUnpaid: boolean,
): { unpaidHours: number; billableHours: number } {
    const unpaidHours = lunchHours + (gapUnpaid ? downtimeHours : 0);
    return { unpaidHours, billableHours: Math.max(0, totalHours - unpaidHours) };
}
